/**
 * O que dizer quando o login por senha falha — puro, sem Supabase.
 *
 * ── Uma frase só para "não existe" e "senha errada" ──────────────────────
 *
 * "Esse email não tem conta" entrega, a quem estiver tentando emails, quais
 * deles são de gente que usa o app. O Supabase já devolve o mesmo código para
 * os dois casos (`invalid_credentials`), e esta tabela mantém isso: nenhuma
 * frase aqui confirma ou nega que um email existe.
 *
 * ── Por CÓDIGO, não pelo texto ───────────────────────────────────────────
 *
 * O texto do erro é em inglês e muda entre versões da biblioteca. O `code` é
 * o contrato. O que não estiver na tabela cai na frase genérica — melhor
 * dizer "não consegui" do que mostrar inglês cru para ela.
 */
export interface ErroDoSupabase {
  code?: string | null;
  status?: number | null;
  message?: string | null;
}

export const SENHA_MINIMA = 8;

const FRASES: Record<string, string> = {
  invalid_credentials: 'Email ou senha incorretos.',
  email_not_confirmed: 'Falta confirmar seu email. Abra o link que enviamos e tente de novo.',
  weak_password: `A senha precisa ter pelo menos ${SENHA_MINIMA} caracteres.`,
  same_password: 'A senha nova precisa ser diferente da atual.',
  over_email_send_rate_limit: 'Já enviamos emails demais para esse endereço. Espere alguns minutos e tente de novo.',
  over_request_rate_limit: 'Muitas tentativas seguidas. Espere um pouco e tente de novo.',
  email_address_invalid: 'Esse email não parece válido. Confira e tente de novo.',
  validation_failed: 'Confira o email e a senha e tente de novo.',
  otp_expired: 'Esse link venceu. Peça um novo.',
  session_not_found: 'Esse link venceu. Peça um novo.',
};

export const GENERICA = 'Não consegui agora. Tente de novo em instantes.';

export const mensagemDeAuth = (erro: ErroDoSupabase | null | undefined): string => {
  if (!erro) return GENERICA;
  if (erro.code && FRASES[erro.code]) return FRASES[erro.code];
  // 429 sem código: algum proxy no caminho comeu o corpo.
  if (erro.status === 429) return FRASES.over_request_rate_limit;
  return GENERICA;
};

/** O que dá para recusar antes de ir ao servidor. `null` = pode enviar. */
export const validarCadastro = (p: { nome: string; email: string; senha: string }): string | null => {
  if (!p.nome.trim()) return 'Diga como podemos te chamar.';
  if (!emailPlausivel(p.email)) return 'Esse email não parece válido. Confira e tente de novo.';
  if (p.senha.length < SENHA_MINIMA) return `A senha precisa ter pelo menos ${SENHA_MINIMA} caracteres.`;
  return null;
};

/**
 * Só o formato óbvio: algo, arroba, algo com ponto. Quem decide se o email
 * existe é o link de confirmação — uma regra mais esperta aqui só recusaria
 * endereços válidos que ela não previu.
 */
export const emailPlausivel = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
